import { api, fmtBRL, fmtTime, fmtDate, STATUS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/Combobox";
import { Pencil, Trash, Trash2, Users2, MessageCircle, MessageSquare, Camera, Package, Calendar, ChevronLeft, ChevronRight, Loader2, ArrowUpDown, ArrowUp, ArrowDown, Route as RouteIcon, Navigation, HandCoins, Eye, StickyNote, MessageSquareText, Coins } from "lucide-react";
import { StatusBadge, TempoEmRota, RotaBadge, notifyEmTransito } from "./entregasHelpers";
import { EntregaDetalheDialog } from "./EntregaDetalheDialog";
import { useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/roles";

// Constante de módulo — evita recomputar `Object.entries(STATUS).filter(...)`
// a cada render do Select de status (uma vez POR LINHA da tabela).
const STATUS_OPTIONS = Object.entries(STATUS).filter(([k]) => k !== "aguardando");

// Helpers de render para simplificar ternárias aninhadas (code review fev/2026).
// Aceita canSeeFin como parâmetro pra manter helpers puros/testáveis.
function renderValorCell(it, isColeta, canSeeFin) {
  if (isColeta(it.tipo_id)) return <span className="text-blue-500 text-xs">Coleta</span>;
  if (!canSeeFin) return <span className="text-slate-400" title="Sem permissão para ver valores">****</span>;
  return fmtBRL(it.valor);
}

function renderTrocoCell(it, isColeta, isSemTroco, canSeeFin) {
  if (isColeta(it.tipo_id)) return <span className="text-slate-400 text-xs">—</span>;
  if (isSemTroco(it.forma_pgto_id)) return <span className="text-slate-400 text-xs">Sem troco</span>;
  if (!canSeeFin) return <span className="text-slate-400" title="Sem permissão para ver valores">****</span>;
  return <span className="text-orange-600">{fmtBRL(it.troco)}</span>;
}

// Iter 95: cabeçalho clicável — ordenação no servidor (sort_by/sort_dir).
function SortableHead({ label, sortKey, sort, onToggle, className = "" }) {
  const active = sort?.by === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead className={className}>
      <button type="button" onClick={() => onToggle?.(sortKey)}
        className={`inline-flex items-center gap-1 select-none hover:text-orange-700 transition-colors ${active ? "text-orange-700 font-semibold" : ""}`}
        title={active ? `Ordenado por ${label} (${sort.dir === "asc" ? "crescente" : "decrescente"}) — clique para alternar` : `Ordenar por ${label}`}
        data-testid={`sort-${sortKey}`} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
        {label}
        <Icon className={`w-3 h-3 ${active ? "" : "opacity-40"}`} />
      </button>
    </TableHead>
  );
}

/**
 * Tabela principal de lançamentos + toolbar de ações em massa + linha de cabeçalho.
 * Props muito grandes (recebe callbacks e lookups). Aceita porque é
 * um componente-container isolado da Entregas.jsx.
 */
export function EntregasTable(props) {
  const {
    filtered, filters, selected, entregadores, bairros, regioes, tipos, formas, rotas = [],
    empresa, unreadMap, bulkEntregador, setBulkEntregador, bulkData, setBulkData,
    bulkRota, setBulkRota, bulkAssignRota, otimizarSelecionadas, otimBusy,
    nameOf, isColeta, isSemTroco, isReceitaRevenda,
    toggleSelect, toggleSelectAll, clearSelection,
    bulkAssignEntregador, bulkUpdateData, askBulkDelete, setConfirmClear,
    changeStatus, openEdit, askDelete, markRead, setChatEntrega, setEmpresa,
    total = filtered.length, page = 1, pageSize = 100, setPage, setPageSize, loading = false,
    sort = null, toggleSort,
  } = props;
  const { user } = useAuth();
  const [detalhe, setDetalhe] = useState(null);
  const canDel = hasPerm(user, "entregas", "e");
  // Iter 100: "Definir rota" em lote só para quem pode editar entregas (matriz entregas.a).
  const canEditEnt = hasPerm(user, "entregas", "a");
  // Matrix-first: financeiro.v controla exibição dos valores monetários. Se falso,
  // mostra asteriscos em vez de R$ (usuário vê a lista mas não os valores).
  const canSeeFin = hasPerm(user, "financeiro", "v");
  // Iter 98: lookup rota_id → {nome, cor} pro badge colorido da coluna Rota.
  const rotaById = useMemo(() => new Map(rotas.map((r) => [r.id, r])), [rotas]);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const firstIdx = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastIdx = Math.min(total, (page - 1) * pageSize + filtered.length);

  // Renderiza a coluna "Nº Pedido". Se pedidos_multiplos tem N >= 2 itens, mostra o
  // primeiro em destaque + chip "+N-1" (com tooltip listando TODOS). Também suporta
  // o legado Receita+Revenda. Semântica nova (fev/2026): pedidos_multiplos = lista
  // completa dos N números; numero_pedido é sincronizado com o primeiro.
  const renderNumeroPedido = (it) => {
    if (isReceitaRevenda(it.tipo_id)) {
      return (
        <div className="flex flex-col gap-0.5" data-testid={`pedido-dual-${it.id}`}>
          <span title="Receita"><span className="text-[9px] uppercase text-emerald-700 mr-1">Rec</span>{it.numero_pedido_receita || "—"}</span>
          <span title="Revenda"><span className="text-[9px] uppercase text-blue-700 mr-1">Rev</span>{it.numero_pedido_revenda || "—"}</span>
        </div>
      );
    }
    const peds = Array.isArray(it.pedidos_multiplos) ? it.pedidos_multiplos.filter(Boolean) : [];
    const qtd = Number(it.qtd_pedidos) || (peds.length || 1);
    if (peds.length >= 2) {
      const first = peds[0];
      const extras = peds.length - 1;
      return (
        <div className="flex items-center gap-1" data-testid={`pedido-multi-${it.id}`} data-qtd-pedidos={qtd} title={peds.join(", ")}>
          <span className="font-medium">{first}</span>
          <span className="inline-flex items-center gap-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-300 text-[9px] font-bold uppercase px-1.5 py-0.5">
            <Package className="w-2.5 h-2.5" />+{extras}
          </span>
        </div>
      );
    }
    return it.numero_pedido || "—";
  };

  return (
    <Card className="flex-1 min-h-0 flex flex-col" data-testid="entregas-table-card">
      <CardHeader className="flex flex-row justify-between items-center gap-3 flex-wrap py-3 px-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <span data-testid="entregas-total">Lançamentos ({total}</span>
          {filters.start || filters.end ? ` · ${filters.start ? filters.start.split("-").reverse().join("/") : ""}${filters.start && filters.end ? " a " : ""}${filters.end ? filters.end.split("-").reverse().join("/") : ""}` : ""})
          {loading && <Loader2 className="w-4 h-4 animate-spin text-orange-500" data-testid="entregas-loading" />}
        </CardTitle>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-1.5 flex-wrap">
              <span className="text-sm font-semibold text-orange-700">{selected.size} selecionada(s)</span>
              <div className="w-52">
                <Combobox options={entregadores} value={bulkEntregador} onChange={setBulkEntregador}
                  placeholder="Atribuir entregador" testId="bulk-entregador" allowClear={false} />
              </div>
              <Button size="sm" onClick={bulkAssignEntregador} className="bg-orange-600 hover:bg-orange-700 h-8" data-testid="btn-bulk-assign"><Users2 className="w-3 h-3 mr-1" />Atribuir</Button>
              <div className="flex items-center gap-1">
                <Input type="date" value={bulkData} onChange={(e) => setBulkData(e.target.value)}
                  onClick={(e) => { try { e.target.showPicker && e.target.showPicker(); } catch (err) { console.debug("showPicker indisponível", err); } }}
                  className="h-8 w-40 text-sm" data-testid="bulk-data" />
                <Button size="sm" variant="outline" onClick={bulkUpdateData} disabled={!bulkData} className="h-8 border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-40" data-testid="btn-bulk-data">
                  <Calendar className="w-3 h-3 mr-1" />Mudar data
                </Button>
              </div>
              {canEditEnt && rotas.length > 0 && (
                <div className="flex items-center gap-1" data-testid="bulk-rota-group">
                  <Select value={bulkRota || ""} onValueChange={setBulkRota}>
                    <SelectTrigger className="h-8 w-44 text-xs bg-white" data-testid="bulk-rota"><SelectValue placeholder="Rota…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__" data-testid="bulk-rota-opt-none">Sem rota</SelectItem>
                      {rotas.map((r) => (
                        <SelectItem key={r.id} value={r.id} data-testid={`bulk-rota-opt-${r.id}`}>
                          <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: r.cor }} />{r.nome}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" onClick={bulkAssignRota} disabled={!bulkRota} className="h-8 border-violet-300 text-violet-700 hover:bg-violet-50 disabled:opacity-40" data-testid="btn-bulk-rota">
                    <RouteIcon className="w-3 h-3 mr-1" />Definir rota
                  </Button>
                </div>
              )}
              <Button size="sm" variant="outline" onClick={otimizarSelecionadas} disabled={otimBusy || selected.size < 2} title="Calcula a melhor sequência de visita (mesmo entregador) e salva a ordem para o app"
                className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40" data-testid="btn-bulk-otimizar">
                {otimBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Navigation className="w-3 h-3 mr-1" />}Otimizar sequência
              </Button>
              <Button size="sm" variant="outline" onClick={askBulkDelete} disabled={!canDel} className="h-8 text-rose-600 border-rose-300 hover:bg-rose-50 disabled:opacity-40" data-testid="btn-bulk-delete"><Trash2 className="w-3 h-3 mr-1" />Excluir sel.</Button>
              <Button size="sm" variant="ghost" onClick={clearSelection} className="h-8">Cancelar</Button>
            </div>
          )}
          <Button size="sm" variant="outline" onClick={() => setConfirmClear(true)} className="text-rose-600 border-rose-300 hover:bg-rose-50" data-testid="btn-clear-all" disabled={filtered.length === 0}>
            <Trash className="w-4 h-4 mr-1" />Limpar tudo
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 flex flex-col p-0" data-testid="entregas-table-scroll-area">
        {/* Iter 116: tabela ocupa toda a altura disponível — cabeçalho fixo + scroll interno (vertical/horizontal) */}
        <Table wrapperClassName="flex-1 min-h-0 overflow-auto px-2" data-testid="entregas-table">
          <TableHeader className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_#e2e8f0]">
            <TableRow className="hover:bg-white">
              <TableHead className="w-8"><Checkbox checked={filtered.length > 0 && selected.size === filtered.length} onCheckedChange={toggleSelectAll} data-testid="select-all" /></TableHead>
              <SortableHead label="Data" sortKey="data" sort={sort} onToggle={toggleSort} />
              <SortableHead label="Entregador" sortKey="entregador" sort={sort} onToggle={toggleSort} />
              <SortableHead label="Bairro" sortKey="bairro" sort={sort} onToggle={toggleSort} />
              <SortableHead label="Rota" sortKey="rota" sort={sort} onToggle={toggleSort} />
              <TableHead>Região</TableHead><TableHead>Tipo</TableHead><TableHead>Forma Pgto</TableHead>
              <SortableHead label="Nº Pedido" sortKey="numero_pedido" sort={sort} onToggle={toggleSort} />
              <SortableHead label="Cliente" sortKey="cliente_nome" sort={sort} onToggle={toggleSort} />
              <TableHead>Receita?</TableHead>
              <SortableHead label="Valor" sortKey="valor" sort={sort} onToggle={toggleSort} />
              <TableHead>Troco</TableHead>
              <SortableHead label="HR Saída" sortKey="hr_saida" sort={sort} onToggle={toggleSort} />
              <SortableHead label="HR Chegada" sortKey="hr_chegada" sort={sort} onToggle={toggleSort} />
              <SortableHead label="Status" sortKey="status" sort={sort} onToggle={toggleSort} />
              <TableHead>Ação</TableHead>
              <TableHead>Motivo / Obs.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((it) => (
              <TableRow key={it.id} className={selected.has(it.id) ? "bg-orange-50/50" : ""}>
                <TableCell><Checkbox checked={selected.has(it.id)} onCheckedChange={() => toggleSelect(it.id)} data-testid={`select-${it.id}`} /></TableCell>
                <TableCell className="text-xs mono" data-testid={`row-data-${it.id}`}>
                  {it.data ? it.data.split("-").reverse().join("/") : fmtDate(it.created_at)}
                </TableCell>
                <TableCell className="font-medium">{nameOf(entregadores, it.entregador_id)}</TableCell>
                <TableCell>{nameOf(bairros, it.bairro_id)}</TableCell>
                <TableCell><RotaBadge rota={rotaById.get(it.rota_id)} testId={`rota-badge-${it.id}`} /></TableCell>
                <TableCell>{nameOf(regioes, it.regiao_id)}</TableCell>
                <TableCell>{nameOf(tipos, it.tipo_id)}</TableCell>
                <TableCell className="text-xs">{nameOf(formas, it.forma_pgto_id) || "—"}</TableCell>
                <TableCell className="mono text-xs">{renderNumeroPedido(it)}</TableCell>
                <TableCell className="text-xs">
                  {it.cliente_nome ? <div className="font-medium">{it.cliente_nome}</div> : <span className="text-slate-300">—</span>}
                  {it.cliente_telefone && <div className="text-slate-500 mono">{it.cliente_telefone}</div>}
                </TableCell>
                <TableCell className="text-xs">
                  {it.buscar_receita ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border">Sim</Badge> : <span className="text-slate-300">—</span>}
                </TableCell>
                <TableCell className="mono">{renderValorCell(it, isColeta, canSeeFin)}</TableCell>
                <TableCell className="mono">{renderTrocoCell(it, isColeta, isSemTroco, canSeeFin)}</TableCell>
                <TableCell className="text-xs mono">{it.hr_saida ? fmtTime(it.hr_saida) : "—"}</TableCell>
                <TableCell className="text-xs mono">
                  {it.hr_chegada ? fmtTime(it.hr_chegada) : "—"}
                  {it.latitude && it.longitude && (
                    <a href={`https://www.google.com/maps?q=${it.latitude},${it.longitude}`} target="_blank" rel="noreferrer" className="ml-1 text-blue-600 hover:underline" data-testid={`gps-${it.id}`} title={`GPS: ${it.latitude.toFixed(5)}, ${it.longitude.toFixed(5)} · Clique para ver no Google Maps`}>📍</a>
                  )}
                </TableCell>
                <TableCell>
                  <StatusBadge s={it.status} />
                  <TempoEmRota hr_saida={it.hr_saida} status={it.status} />
                  {it.acerto_id && (
                    <span className="ml-1 align-middle inline-flex items-center gap-0.5 text-[9px] font-bold uppercase bg-emerald-100 text-emerald-800 border border-emerald-300 px-1.5 py-0.5 rounded-full" title="Já entrou em um acerto com o entregador" data-testid={`acertado-${it.id}`}>
                      <HandCoins className="w-2.5 h-2.5" />Acertado
                    </span>
                  )}
                  {it.foto_evidencia_path && (
                    <button onClick={() => openEdit(it)} title="Ver foto de evidência" className="ml-1 align-middle inline-flex items-center text-orange-600 hover:text-orange-800" data-testid={`evidencia-badge-${it.id}`}>
                      <Camera className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {it.prioridade === "urgente" && (
                    <span className="ml-1 align-middle inline-flex items-center gap-0.5 text-[9px] font-extrabold uppercase bg-red-600 text-white px-1.5 py-0.5 rounded-full animate-pulse" data-testid={`prio-urgente-${it.id}`}>🚨 Urg.</span>
                  )}
                  {it.prioridade === "vip" && (
                    <span className="ml-1 align-middle inline-flex items-center gap-0.5 text-[9px] font-extrabold uppercase bg-purple-600 text-white px-1.5 py-0.5 rounded-full" data-testid={`prio-vip-${it.id}`}>⭐ VIP</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1 items-center">
                    <Select value={it.status || "aguardando"} onValueChange={(v) => changeStatus(it, v)}>
                      <SelectTrigger className="w-32 h-8 text-xs" data-testid={`status-${it.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="icon" variant="ghost" onClick={() => { markRead(it.id); setChatEntrega(it); }} data-testid={`btn-chat-${it.id}`}
                      title={unreadMap[it.id] ? `${unreadMap[it.id].count} nova(s) mensagem(ns) de ${unreadMap[it.id].last_sender || "entregador"}` : "Chat com entregador"}
                      className={`relative ${unreadMap[it.id] ? "bg-red-50 hover:bg-red-100 chat-ring-pulse" : ""}`}>
                      <MessageSquare className={`w-4 h-4 ${unreadMap[it.id] ? "text-red-600 chat-blink" : "text-blue-600"}`} />
                      {unreadMap[it.id] && (
                        <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[10px] font-extrabold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center shadow-md ring-2 ring-white chat-blink" data-testid={`chat-badge-${it.id}`}>
                          {unreadMap[it.id].count > 9 ? "9+" : unreadMap[it.id].count}
                        </span>
                      )}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => setDetalhe(it)} data-testid={`btn-detalhe-${it.id}`} title="Detalhe da entrega (observações, troco, comprovantes)"><Eye className="w-4 h-4 text-slate-600" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`btn-edit-entrega-${it.id}`}><Pencil className="w-4 h-4 text-orange-600" /></Button>
                    {it.status === "em_transito" && it.cliente_telefone && (() => {
                      const url = notifyEmTransito(it, entregadores, empresa);
                      return url ? (
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`btn-wpp-${it.id}`}
                          title={`Notificar ${it.cliente_nome || "cliente"} via WhatsApp`}
                          className="inline-flex items-center justify-center w-8 h-8 rounded hover:bg-emerald-50"
                          onClick={async (e) => {
                            e.preventDefault();
                            try {
                              const r = await api.get("/empresa");
                              const fresh = notifyEmTransito(it, entregadores, r.data);
                              window.open(fresh || url, "_blank", "noopener,noreferrer");
                              setEmpresa(r.data);
                            } catch {
                              window.open(url, "_blank", "noopener,noreferrer");
                            }
                          }}
                        >
                          <MessageCircle className="w-4 h-4 text-emerald-600" />
                        </a>
                      ) : null;
                    })()}
                    {canDel && <Button size="icon" variant="ghost" onClick={() => askDelete(it.id)} data-testid={`btn-del-entrega-${it.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>}
                  </div>
                </TableCell>
                <TableCell className="text-xs max-w-xs">
                  <div className="flex items-center gap-1">
                    {it.observacao && <button type="button" onClick={() => setDetalhe(it)} title={`Obs. admin: ${it.observacao}`} data-testid={`obs-admin-${it.id}`}><StickyNote className="w-3.5 h-3.5 text-amber-600" /></button>}
                    {it.observacao_entregador && <button type="button" onClick={() => setDetalhe(it)} title={`Obs. entregador: ${it.observacao_entregador}`} data-testid={`obs-entregador-${it.id}`}><MessageSquareText className="w-3.5 h-3.5 text-sky-600" /></button>}
                    {it.troco_saida_confirmado && <span title={`Troco de saída recebido do balcão${it.troco_saida_confirmado_at ? ` às ${fmtTime(it.troco_saida_confirmado_at)}` : ""}`} data-testid={`troco-saida-ok-${it.id}`}><HandCoins className="w-3.5 h-3.5 text-violet-600" /></span>}
                    {it.troco_confirmado && <span title={`Troco entregue ao cliente${it.troco_confirmado_at ? ` às ${fmtTime(it.troco_confirmado_at)}` : ""}`} data-testid={`troco-ok-${it.id}`}><Coins className="w-3.5 h-3.5 text-emerald-600" /></span>}
                    {it.motivo ? <span className="text-slate-700 truncate" title={it.motivo}>{it.motivo}</span> : (!it.observacao && !it.observacao_entregador && !it.troco_confirmado && !it.troco_saida_confirmado) && <span className="text-slate-300">—</span>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={18} className="text-center text-muted-foreground py-8">{loading ? "Carregando…" : "Nenhuma entrega para os filtros atuais"}</TableCell></TableRow>}
          </TableBody>
        </Table>
        {/* Iter 94: paginação server-side — renderiza só a página atual (sem travar em períodos grandes) */}
        <div className="flex items-center justify-between flex-wrap gap-3 px-4 py-2 border-t bg-white shrink-0" data-testid="entregas-pagination">
          <div className="text-xs text-muted-foreground" data-testid="pagination-range">
            Mostrando <b>{firstIdx}–{lastIdx}</b> de <b>{total}</b>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Por página</span>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize?.(Number(v))}>
              <SelectTrigger className="h-8 w-20 text-xs" data-testid="page-size-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[50, 100, 200, 500].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-8 px-2" disabled={page <= 1 || loading}
              onClick={() => setPage?.(page - 1)} data-testid="page-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-xs font-semibold min-w-[90px] text-center" data-testid="page-indicator">Página {page} de {totalPages}</span>
            <Button size="sm" variant="outline" className="h-8 px-2" disabled={page >= totalPages || loading}
              onClick={() => setPage?.(page + 1)} data-testid="page-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </CardContent>
      <EntregaDetalheDialog entrega={detalhe} onClose={() => setDetalhe(null)}
        nomes={detalhe ? { bairro: nameOf(bairros, detalhe.bairro_id), entregador: nameOf(entregadores, detalhe.entregador_id), forma: nameOf(formas, detalhe.forma_pgto_id) } : null} />
    </Card>
  );
}
